import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Loader2, TrendingUp, TrendingDown, ShoppingCart, Euro, Users,
         BarChart2, Database, RefreshCw, Package, Award, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Cell, Legend,
} from 'recharts';

// ── Utilities ──────────────────────────────────────────────────────────────────
const fmtEuro = (v) => {
  const n = Number(v || 0);
  if (n >= 1_000_000) return `€ ${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000)     return `€ ${(n / 1_000).toFixed(1)}k`;
  return `€ ${n.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
const fmtN = (v) => Number(v || 0).toLocaleString('it-IT');

const STATUS_LABELS = {
  draft: 'Bozza', confirmed: 'Confermato', processing: 'In lavoraz.',
  shipped: 'Spedito', delivered: 'Consegnato', cancelled: 'Annullato',
};
const STATUS_COLORS = {
  draft: '#94a3b8', confirmed: '#3b82f6', processing: '#f59e0b',
  shipped: '#a855f7', delivered: '#22c55e', cancelled: '#ef4444',
};

const CHART_COLORS = ['#16a34a', '#3b82f6', '#f59e0b', '#a855f7', '#f97316', '#06b6d4', '#ec4899'];

// ── Sub-components ─────────────────────────────────────────────────────────────

function DeltaBadge({ current, previous, suffix = '' }) {
  if (!previous || previous === 0) {
    if (current > 0) {
      return <div className="text-xs font-medium mt-1 text-blue-500">Primo anno di dati</div>;
    }
    return null;
  }
  const delta = ((current - previous) / previous) * 100;
  const pos = delta >= 0;
  return (
    <div className={`flex items-center gap-0.5 text-xs font-medium mt-1 ${pos ? 'text-green-600 dark:text-green-400' : 'text-red-500'}`}>
      {pos ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
      {Math.abs(delta).toFixed(1)}%{suffix}
    </div>
  );
}

function KpiCard({ label, value, sub, icon: Icon, iconColor = 'text-primary', current, previous, testId }) {
  return (
    <Card data-testid={testId}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <p className="text-xs text-muted-foreground font-medium">{label}</p>
            <p className="text-xl font-bold mt-1 font-[Manrope,sans-serif]">{value}</p>
            {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
            {current !== undefined && previous !== undefined && (
              <DeltaBadge current={current} previous={previous} />
            )}
          </div>
          <div className="w-9 h-9 rounded-xl bg-muted flex items-center justify-center flex-shrink-0 ml-3">
            <Icon size={17} className={iconColor} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function SectionTitle({ children }) {
  return (
    <h2 className="text-xs uppercase font-semibold text-muted-foreground tracking-wider mb-3 mt-2">
      {children}
    </h2>
  );
}

const CustomTooltipEuro = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-border bg-card shadow-md p-3 text-sm">
      <p className="font-semibold mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }}>{p.name}: {fmtEuro(p.value)}</p>
      ))}
    </div>
  );
};

// ── ANNUAL VIEW ────────────────────────────────────────────────────────────────

function AnnualView({ year, setYear }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/analytics/annual`, { params: { year } });
      setData(res.data);
    } catch { toast.error('Errore caricamento dati annuali'); }
    finally { setLoading(false); }
  }, [year]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-primary" size={32} /></div>;
  if (!data) return null;

  const { summary: s, prev_year_summary: p, monthly_trend, top_clients, top_products, by_salesperson, available_years } = data;

  const statusData = Object.entries(s.by_status || {})
    .filter(([k]) => k !== 'cancelled')
    .map(([k, v]) => ({ name: STATUS_LABELS[k] || k, value: v.value, count: v.count, fill: STATUS_COLORS[k] }))
    .sort((a, b) => b.value - a.value);

  // Build a full range: from earliest known year (or 4 years back) to selected year + 1
  const currentCalYear = new Date().getFullYear();
  const minYear = Math.min(
    ...(available_years?.length ? available_years : [currentCalYear]),
    currentCalYear - 3,
    year
  );
  const maxYear = Math.max(currentCalYear, year);
  const selectable_years = Array.from({ length: maxYear - minYear + 1 }, (_, i) => minYear + i);

  return (
    <div className="space-y-6">
      {/* Year selector */}
      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-sm font-medium text-muted-foreground">Anno di riferimento:</span>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setYear(y => y - 1)}
            data-testid="year-prev"
            className="w-7 h-7 flex items-center justify-center rounded-lg border border-border hover:bg-muted transition-colors text-muted-foreground"
          >‹</button>
          <div className="flex gap-1 flex-wrap">
            {selectable_years.map(y => {
              const hasData = (available_years || [data.year]).includes(y);
              return (
                <button
                  key={y}
                  onClick={() => setYear(y)}
                  data-testid={`year-btn-${y}`}
                  className={`px-3 py-1 rounded-lg text-sm font-semibold border transition-all ${
                    y === year
                      ? 'bg-primary text-primary-foreground border-primary'
                      : hasData
                      ? 'border-border hover:bg-muted'
                      : 'border-dashed border-border text-muted-foreground hover:bg-muted/50'
                  }`}
                  title={hasData ? `Dati presenti per ${y}` : `Nessun ordine per ${y}`}
                >
                  {y}
                </button>
              );
            })}
          </div>
          <button
            onClick={() => setYear(y => y + 1)}
            data-testid="year-next"
            className="w-7 h-7 flex items-center justify-center rounded-lg border border-border hover:bg-muted transition-colors text-muted-foreground"
          >›</button>
        </div>
        <span className="text-xs text-muted-foreground ml-auto">vs {year - 1}: confronto anno precedente</span>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          label="Ordini" value={fmtN(s.total_orders)} sub={`${p.total_orders} nel ${year - 1}`}
          icon={ShoppingCart} current={s.total_orders} previous={p.total_orders}
          testId="kpi-orders-annual"
        />
        <KpiCard
          label="Fatturato (IVA incl.)" value={fmtEuro(s.total_revenue)} sub={fmtEuro(p.total_revenue) + ` nel ${year - 1}`}
          icon={Euro} iconColor="text-green-600 dark:text-green-400"
          current={s.total_revenue} previous={p.total_revenue}
          testId="kpi-revenue-annual"
        />
        <KpiCard
          label="Imponibile Netto" value={fmtEuro(s.net_revenue)} sub={`IVA: ${fmtEuro(s.tax_amount)}`}
          icon={BarChart2} iconColor="text-blue-600 dark:text-blue-400"
          current={s.net_revenue} previous={p.net_revenue}
          testId="kpi-net-annual"
        />
        <KpiCard
          label="Valore Medio Ordine" value={fmtEuro(s.avg_order_value)} sub={fmtEuro(p.avg_order_value) + ` nel ${year - 1}`}
          icon={TrendingUp} iconColor="text-amber-600 dark:text-amber-400"
          current={s.avg_order_value} previous={p.avg_order_value}
          testId="kpi-avg-annual"
        />
      </div>

      {/* Monthly Trend */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold font-[Manrope,sans-serif]">
            Andamento Mensile {year}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={monthly_trend} margin={{ top: 4, right: 12, bottom: 0, left: 8 }}>
              <CartesianGrid strokeDasharray="3 3" className="opacity-20" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} width={48} />
              <Tooltip content={<CustomTooltipEuro />} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="revenue" name="Fatturato" fill="#16a34a" radius={[3, 3, 0, 0]} />
              <Bar dataKey="net_revenue" name="Imponibile" fill="#86efac" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Status breakdown + Top clients */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Status */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold font-[Manrope,sans-serif]">Ordini per Stato</CardTitle>
          </CardHeader>
          <CardContent>
            {statusData.length > 0 ? (
              <div className="space-y-2.5">
                {statusData.map(item => {
                  const max = Math.max(...statusData.map(d => d.value));
                  const pct = max > 0 ? (item.value / max) * 100 : 0;
                  return (
                    <div key={item.name}>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <div className="flex items-center gap-2">
                          <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: item.fill }} />
                          <span className="text-muted-foreground">{item.name}</span>
                          <span className="font-medium text-xs">({item.count})</span>
                        </div>
                        <span className="font-semibold">{fmtEuro(item.value)}</span>
                      </div>
                      <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: item.fill }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground py-6 text-center">Nessun ordine per {year}</p>
            )}
          </CardContent>
        </Card>

        {/* Top Clients */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold font-[Manrope,sans-serif]">Top Clienti {year}</CardTitle>
          </CardHeader>
          <CardContent>
            {top_clients?.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={top_clients.slice(0, 7)} layout="vertical" margin={{ top: 0, right: 60, left: 4, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" className="opacity-20" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={90} />
                  <Tooltip formatter={v => [fmtEuro(v), 'Fatturato']} />
                  <Bar dataKey="revenue" radius={[0, 4, 4, 0]}>
                    {top_clients.slice(0, 7).map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-sm text-muted-foreground py-6 text-center">Nessun dato</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Products + Salesperson tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Products */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold font-[Manrope,sans-serif] flex items-center gap-2">
              <Package size={14} className="text-muted-foreground" />Top Prodotti / Servizi {year}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {top_products?.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-muted/40 border-b">
                      <th className="text-left px-4 py-2.5 font-medium text-muted-foreground text-xs">#</th>
                      <th className="text-left px-3 py-2.5 font-medium text-muted-foreground text-xs">Descrizione</th>
                      <th className="text-right px-3 py-2.5 font-medium text-muted-foreground text-xs">Qtà</th>
                      <th className="text-right px-3 py-2.5 font-medium text-muted-foreground text-xs">Fatturato</th>
                      <th className="text-right px-4 py-2.5 font-medium text-muted-foreground text-xs">Ordini</th>
                    </tr>
                  </thead>
                  <tbody>
                    {top_products.map((p, i) => (
                      <tr key={i} className="border-b last:border-0 hover:bg-muted/20 transition-colors">
                        <td className="px-4 py-2.5 text-muted-foreground text-xs font-mono">{i + 1}</td>
                        <td className="px-3 py-2.5 font-medium max-w-[180px] truncate">{p.description}</td>
                        <td className="px-3 py-2.5 text-right text-muted-foreground">{fmtN(p.qty)}</td>
                        <td className="px-3 py-2.5 text-right font-semibold text-green-700 dark:text-green-400">{fmtEuro(p.revenue)}</td>
                        <td className="px-4 py-2.5 text-right text-muted-foreground">{p.order_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground py-6 text-center px-4">Nessun prodotto per {year}</p>
            )}
          </CardContent>
        </Card>

        {/* Salesperson */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold font-[Manrope,sans-serif] flex items-center gap-2">
              <Award size={14} className="text-muted-foreground" />Performance Commerciali {year}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {by_salesperson?.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-muted/40 border-b">
                      <th className="text-left px-4 py-2.5 font-medium text-muted-foreground text-xs">#</th>
                      <th className="text-left px-3 py-2.5 font-medium text-muted-foreground text-xs">Commerciale</th>
                      <th className="text-right px-3 py-2.5 font-medium text-muted-foreground text-xs">Ordini</th>
                      <th className="text-right px-3 py-2.5 font-medium text-muted-foreground text-xs">Fatturato</th>
                      <th className="text-right px-4 py-2.5 font-medium text-muted-foreground text-xs">Medio</th>
                    </tr>
                  </thead>
                  <tbody>
                    {by_salesperson.map((rep, i) => (
                      <tr key={i} className="border-b last:border-0 hover:bg-muted/20 transition-colors">
                        <td className="px-4 py-2.5 text-muted-foreground text-xs font-mono">{i + 1}</td>
                        <td className="px-3 py-2.5 font-medium">{rep.name}</td>
                        <td className="px-3 py-2.5 text-right">{rep.orders}</td>
                        <td className="px-3 py-2.5 text-right font-semibold text-green-700 dark:text-green-400">{fmtEuro(rep.revenue)}</td>
                        <td className="px-4 py-2.5 text-right text-muted-foreground">
                          {rep.orders > 0 ? fmtEuro(rep.revenue / rep.orders) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground py-6 text-center px-4">Nessun dato</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ── CUMULATIVE VIEW ────────────────────────────────────────────────────────────

function CumulativeView() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/analytics/cumulative`)
      .then(r => setData(r.data))
      .catch(() => toast.error('Errore caricamento storico'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-primary" size={32} /></div>;
  if (!data) return null;

  const { summary: s, annual_trend, client_lifetime_value, top_products, by_salesperson } = data;

  return (
    <div className="space-y-6">
      {/* KPI Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label="Ordini Totali" value={fmtN(s.total_orders)} sub="Storico complessivo"
          icon={ShoppingCart} testId="kpi-orders-cumul" />
        <KpiCard label="Fatturato Storico" value={fmtEuro(s.total_revenue)} sub={`Imponibile: ${fmtEuro(s.net_revenue)}`}
          icon={Euro} iconColor="text-green-600 dark:text-green-400" testId="kpi-revenue-cumul" />
        <KpiCard label="Clienti Attivi" value={fmtN(s.active_clients)} sub="Con almeno 1 ordine"
          icon={Users} iconColor="text-blue-600 dark:text-blue-400" testId="kpi-clients-cumul" />
        <KpiCard label="Valore Medio Ordine" value={fmtEuro(s.avg_order_value)} sub="Media storica"
          icon={TrendingUp} iconColor="text-amber-600 dark:text-amber-400" testId="kpi-avg-cumul" />
      </div>

      {/* Annual Trend Chart */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold font-[Manrope,sans-serif]">
            Trend Annuale — Tutti gli Anni
          </CardTitle>
        </CardHeader>
        <CardContent>
          {annual_trend?.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={annual_trend} margin={{ top: 4, right: 12, bottom: 0, left: 8 }}>
                <CartesianGrid strokeDasharray="3 3" className="opacity-20" />
                <XAxis dataKey="year" tick={{ fontSize: 12, fontWeight: 600 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} width={52} />
                <Tooltip content={<CustomTooltipEuro />} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="revenue" name="Fatturato" fill="#16a34a" radius={[4, 4, 0, 0]}>
                  {annual_trend.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-sm text-muted-foreground py-8 text-center">Nessun dato storico disponibile</p>
          )}
        </CardContent>
      </Card>

      {/* Client Lifetime Value */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold font-[Manrope,sans-serif] flex items-center gap-2">
            <Users size={14} className="text-muted-foreground" />Valore Lifetime Cliente (LTV)
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {client_lifetime_value?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted/40 border-b">
                    <th className="text-left px-4 py-2.5 font-medium text-muted-foreground text-xs">#</th>
                    <th className="text-left px-3 py-2.5 font-medium text-muted-foreground text-xs">Cliente</th>
                    <th className="text-right px-3 py-2.5 font-medium text-muted-foreground text-xs">Ordini</th>
                    <th className="text-right px-3 py-2.5 font-medium text-muted-foreground text-xs">Totale Speso</th>
                    <th className="text-right px-3 py-2.5 font-medium text-muted-foreground text-xs">Medio Ordine</th>
                    <th className="text-right px-3 py-2.5 font-medium text-muted-foreground text-xs hidden lg:table-cell">Primo Ordine</th>
                    <th className="text-right px-3 py-2.5 font-medium text-muted-foreground text-xs hidden lg:table-cell">Ultimo Ordine</th>
                    <th className="text-right px-4 py-2.5 font-medium text-muted-foreground text-xs hidden xl:table-cell">Freq. (giorni)</th>
                  </tr>
                </thead>
                <tbody>
                  {client_lifetime_value.map((c, i) => (
                    <tr key={i} className="border-b last:border-0 hover:bg-muted/20 transition-colors">
                      <td className="px-4 py-2.5 text-muted-foreground text-xs font-mono">{i + 1}</td>
                      <td className="px-3 py-2.5 font-medium">{c.name}</td>
                      <td className="px-3 py-2.5 text-right font-semibold">{c.orders}</td>
                      <td className="px-3 py-2.5 text-right font-bold text-green-700 dark:text-green-400">{fmtEuro(c.revenue)}</td>
                      <td className="px-3 py-2.5 text-right text-muted-foreground">{fmtEuro(c.avg_order_value)}</td>
                      <td className="px-3 py-2.5 text-right text-muted-foreground hidden lg:table-cell text-xs">
                        {c.first_order ? new Date(c.first_order).toLocaleDateString('it-IT') : '—'}
                      </td>
                      <td className="px-3 py-2.5 text-right text-muted-foreground hidden lg:table-cell text-xs">
                        {c.last_order ? new Date(c.last_order).toLocaleDateString('it-IT') : '—'}
                      </td>
                      <td className="px-4 py-2.5 text-right text-muted-foreground hidden xl:table-cell text-xs">
                        {c.avg_days_between_orders ? `~${c.avg_days_between_orders}gg` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground py-6 text-center px-4">Nessun dato cliente disponibile</p>
          )}
        </CardContent>
      </Card>

      {/* Products + Salesperson All-time */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Products All-time */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold font-[Manrope,sans-serif] flex items-center gap-2">
              <Package size={14} className="text-muted-foreground" />Top Prodotti — Storico
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {top_products?.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-muted/40 border-b">
                      <th className="text-left px-4 py-2.5 font-medium text-muted-foreground text-xs">#</th>
                      <th className="text-left px-3 py-2.5 font-medium text-muted-foreground text-xs">Prodotto / Servizio</th>
                      <th className="text-right px-3 py-2.5 font-medium text-muted-foreground text-xs">Qtà Tot.</th>
                      <th className="text-right px-4 py-2.5 font-medium text-muted-foreground text-xs">Fatturato</th>
                    </tr>
                  </thead>
                  <tbody>
                    {top_products.map((p, i) => (
                      <tr key={i} className="border-b last:border-0 hover:bg-muted/20 transition-colors">
                        <td className="px-4 py-2.5 text-muted-foreground text-xs font-mono">{i + 1}</td>
                        <td className="px-3 py-2.5 font-medium max-w-[180px] truncate">{p.description}</td>
                        <td className="px-3 py-2.5 text-right text-muted-foreground">{fmtN(p.qty)}</td>
                        <td className="px-4 py-2.5 text-right font-bold text-green-700 dark:text-green-400">{fmtEuro(p.revenue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground py-6 text-center px-4">Nessun dato</p>
            )}
          </CardContent>
        </Card>

        {/* Salesperson All-time */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold font-[Manrope,sans-serif] flex items-center gap-2">
              <Award size={14} className="text-muted-foreground" />Performance Commerciali — Storico
            </CardTitle>
          </CardHeader>
          <CardContent>
            {by_salesperson?.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={by_salesperson.slice(0, 8)} layout="vertical" margin={{ top: 0, right: 60, left: 4, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" className="opacity-20" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={90} />
                  <Tooltip formatter={v => [fmtEuro(v), 'Fatturato']} />
                  <Bar dataKey="revenue" radius={[0, 4, 4, 0]}>
                    {by_salesperson.slice(0, 8).map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-sm text-muted-foreground py-6 text-center">Nessun dato</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ── MAIN PAGE ──────────────────────────────────────────────────────────────────

export default function Analytics() {
  const [viewMode, setViewMode] = useState('annual');
  const [year, setYear] = useState(new Date().getFullYear());

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold font-[Manrope,sans-serif]">Analisi Ordini</h1>
          <p className="text-muted-foreground text-sm mt-1">
            {viewMode === 'annual'
              ? `Statistiche annuali — Anno ${year} vs ${year - 1}`
              : 'Dati storici cumulativi senza azzeramento'}
          </p>
        </div>

        {/* Mode switch */}
        <div className="flex items-center gap-1 p-1 rounded-xl bg-muted border border-border flex-shrink-0" data-testid="view-mode-switch">
          <button
            onClick={() => setViewMode('annual')}
            data-testid="btn-annual-view"
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              viewMode === 'annual'
                ? 'bg-card shadow-sm text-foreground ring-1 ring-border'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Calendar size={14} />
            Vista Annuale
          </button>
          <button
            onClick={() => setViewMode('cumulative')}
            data-testid="btn-cumulative-view"
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              viewMode === 'cumulative'
                ? 'bg-card shadow-sm text-foreground ring-1 ring-border'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Database size={14} />
            Vista Cumulativa
          </button>
        </div>
      </div>

      {/* View content */}
      {viewMode === 'annual'
        ? <AnnualView year={year} setYear={setYear} />
        : <CumulativeView />
      }
    </div>
  );
}
